import React, { useState } from 'react';
import { Form, message } from 'antd';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import type { TaskStore } from '../../../stores/kanban/TaskStore';
import type { KanbanTask } from '../../../stores/KanbanTaskStores';
import TimeTabContent from '../TimeTabContent';

export type TaskTimeTabProps = {
  task: KanbanTask;
  store: TaskStore;
  readOnly: boolean;
  projectName?: string;
};

function asHHmm(value: unknown, fallback: string): string {
  if (!value) {
    return fallback;
  }
  if (typeof value === 'string') {
    return value;
  }
  const withFormat = value as { format?: (pattern: string) => string };
  return typeof withFormat.format === 'function' ? withFormat.format('HH:mm') : fallback;
}

/**
 * Wires the untouched `TimeTabContent` (props unchanged) to `TaskStore` — it owns the two forms
 * and the log/estimate handlers the modal used to keep at the page level.
 */
const TaskTimeTab: React.FC<TaskTimeTabProps> = observer(({ task, store, readOnly, projectName }) => {
  const { t } = useTranslation();
  const [timeLoggingForm] = Form.useForm();
  const [estimatedTimeForm] = Form.useForm();
  const [isLoggingTime, setIsLoggingTime] = useState(false);
  const [isEditingEstimatedTime, setIsEditingEstimatedTime] = useState(false);

  // Both handlers are optional on `TaskStore`: the public board has no write endpoint for time, so
  // a store that cannot log time must not be offered the Log Time / estimate controls at all —
  // otherwise the buttons open forms whose Save silently does nothing.
  const canWriteTime = !!store.logTimeForTask && !!store.updateEstimatedTime;

  const onLogTime = async (values: any) => {
    if (!store.logTimeForTask) {
      return;
    }
    try {
      const time = asHHmm(values.time, '00:00');
      const endDateStr = values.endDate ? values.endDate.format('YYYY-MM-DD') : undefined;
      const success = await store.logTimeForTask(task.id, time, values.description, undefined, endDateStr);
      if (success) {
        message.success(t('TimeLogged'));
        timeLoggingForm.resetFields();
        setIsLoggingTime(false);
      }
    } catch (error) {
      console.error('Error logging time:', error);
      message.error(t('FailedToLogTime'));
    }
  };

  const onUpdateEstimatedTime = async (values: any) => {
    if (!store.updateEstimatedTime) {
      return;
    }
    try {
      if (!values.estimatedTime) {
        message.error(t('InvalidTimeValue'));
        return;
      }
      const estimatedTime = asHHmm(values.estimatedTime, '00:00');
      const success = await store.updateEstimatedTime(task.id, estimatedTime);
      if (success) {
        message.success(t('EstimatedTimeUpdated'));
        setIsEditingEstimatedTime(false);
      }
    } catch (error) {
      console.error('Error updating estimated time:', error);
      message.error(t('FailedToUpdateEstimatedTime'));
    }
  };

  return (
    <TimeTabContent
      selectedTask={task}
      taskTimeMetrics={store.taskTimeMetrics}
      taskTimeLogs={store.taskTimeLogs}
      onLogTime={onLogTime}
      onUpdateEstimatedTime={onUpdateEstimatedTime}
      timeLoggingForm={timeLoggingForm}
      estimatedTimeForm={estimatedTimeForm}
      isLoggingTime={isLoggingTime}
      setIsLoggingTime={setIsLoggingTime}
      isEditingEstimatedTime={isEditingEstimatedTime}
      setIsEditingEstimatedTime={setIsEditingEstimatedTime}
      t={t}
      projectName={projectName}
      isReadOnly={readOnly || !canWriteTime}
    />
  );
});

export default TaskTimeTab;
